// Cabecera de un pedido de venta para la pantalla dedicada.
//
// ESTE HOOK ES UNA COSTURA A PROPÓSITO. Hoy no hay endpoint de cabecera
// individual: `GET /v1/pedidos-ventas` sólo filtra por fechas, así que el dato
// se resuelve en cascada. El día que exista un endpoint propio, se reescribe el
// cuerpo de esta función y NADA más — ni la página ni los componentes de
// `detalle/` conocen de dónde sale la cabecera, sólo el `PedidoVentaCabeceraVM`
// que devuelve.
//
// La cascada, de más barata a más pobre:
//   1. `location.state.row` — la fila que el enlace «Ver detalle» ya traía en la
//      mano. Coste cero y datos completos.
//   2. La lista sin filtro de fechas: misma queryKey `['pedidos-ventas','','']`
//      que usa la página de lista por defecto, así que lo normal es que ya esté
//      en caché y no se dispare ninguna petición.
//   3. Reconstruida de las líneas del pedido, marcada como `derivada`: sirve
//      para no dejar la pantalla en blanco, pero le faltan cliente, estado e
//      importes de cabecera y la pantalla lo avisa.
import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { usePedidosVentas } from '@/hooks/usePedidosVentas'
import type {
  OrigenCabeceraPedido, PedidoVentaCabeceraVM, PedidoVentaLinea, PedidoVentaRow,
} from '@/types'

/** Estado de navegación que deja el enlace «Ver detalle» de la lista. */
export interface EstadoNavegacionDetalle {
  row?: PedidoVentaRow
  /** Ruta + query de la lista, para poder volver a ella tal y como estaba. */
  from?: string
}

function deLaFila(r: PedidoVentaRow): PedidoVentaCabeceraVM {
  return {
    empresa: r.dataareaid,
    salesid: r.salesid,
    custaccount: r.custaccount,
    nombreCliente: r.nombreCliente,
    customerRef: r.customerRef,
    estado: r.salesstatus,
    fechaPedido: r.fechaPedido,
    fechaEntrega: r.fechaEntrega,
    qtyPedida: r.qtyPedida,
    qtyServida: r.qtyServida,
    qtyPendiente: r.qtyPendiente,
    importeTotal: r.importeTotal,
    importePendiente: r.importePendiente,
    moneda: r.moneda,
    familia: r.familia,
    subfamilia: r.subfamilia,
    grupoCliente: r.grupoCliente,
    pendienteSync: r.pendienteSync,
  }
}

/** Último recurso: lo que se puede afirmar mirando sólo las líneas.
 *
 *  Vive fuera del hook porque el tercer escalón necesita las líneas y las líneas
 *  necesitan saber si el pedido está abierto —que sale de la cabecera—, así que
 *  encadenarlo dentro sería una dependencia circular. La página llama primero al
 *  hook, luego pide las líneas y sólo entonces recurre a esto. Sigue siendo el
 *  mismo módulo: cuando haya endpoint individual, desaparece de aquí y ya está.
 *
 *  Las cantidades se suman por FILA (`qtyPedida`, `qtyServida`, `importeNeto`),
 *  que es lo único sumable: `qtyLinea` y `qtySinFacturar` son de nivel línea y
 *  se repiten en cada factura. Cliente, referencia y estado no están en las
 *  líneas y se quedan vacíos: por eso el origen se marca como `derivada`. */
export function cabeceraDeLineas(
  empresa: string,
  salesid: string,
  lineas: PedidoVentaLinea[],
): PedidoVentaCabeceraVM {
  return {
    empresa: lineas[0]?.dataareaid ?? empresa,
    salesid,
    moneda: lineas.find(l => l.moneda)?.moneda,
    familia: lineas.find(l => l.familia)?.familia,
    fechaEntrega: lineas.find(l => l.fechaEntrega)?.fechaEntrega,
    qtyPedida: lineas.reduce((s, l) => s + l.qtyPedida, 0),
    qtyServida: lineas.reduce((s, l) => s + l.qtyServida, 0),
    qtyPendiente: lineas.reduce((s, l) => s + l.qtyPendiente, 0),
    importeTotal: lineas.reduce((s, l) => s + (l.importeNeto ?? 0), 0),
    importePendiente: lineas.reduce((s, l) => s + (l.importePendiente ?? 0), 0),
  }
}

/** La cabecera, de vuelta con la forma de fila de la lista.
 *
 *  Existe para que el cockpit pueda llamar a `exportarPedidosVenta` con el
 *  pedido que se está mirando: es la MISMA exportación que la lista, y mantener
 *  dos era garantizar que un día dejaran de cuadrar. La conversión no pierde
 *  nada de lo que el libro usa —`articulosBusqueda` y `tieneLineaArmaf` sólo
 *  sirven para buscar y para pintar en pantalla—. */
export function filaDeCabecera(c: PedidoVentaCabeceraVM): PedidoVentaRow {
  return {
    dataareaid: c.empresa,
    salesid: c.salesid,
    custaccount: c.custaccount ?? '',
    nombreCliente: c.nombreCliente,
    customerRef: c.customerRef,
    fechaPedido: c.fechaPedido,
    fechaEntrega: c.fechaEntrega,
    salesstatus: c.estado,
    familia: c.familia,
    subfamilia: c.subfamilia,
    grupoCliente: c.grupoCliente,
    qtyPedida: c.qtyPedida,
    qtyServida: c.qtyServida,
    qtyPendiente: c.qtyPendiente,
    importeTotal: c.importeTotal,
    importePendiente: c.importePendiente,
    moneda: c.moneda,
    pendienteSync: c.pendienteSync,
  }
}

export interface CabeceraPedidoVenta {
  cabecera: PedidoVentaCabeceraVM | null
  origen: OrigenCabeceraPedido | null
  /** Ruta de la lista de la que venimos, si venimos de ella. */
  volverA?: string
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

export function useCabeceraPedidoVenta(
  empresa: string,
  salesid: string,
): CabeceraPedidoVenta {
  const location = useLocation()
  const estado = (location.state ?? undefined) as EstadoNavegacionDetalle | undefined
  const filaDelEstado = estado?.row

  // Segundo escalón. Se salta si el primero ya resolvió: sin fechas, la queryKey
  // es la que la lista usa por defecto, así que casi siempre es un acierto de
  // caché y no una petición nueva.
  const debeConsultarLista = !filaDelEstado
  const lista = usePedidosVentas('', '', { enabled: debeConsultarLista })

  const deLaLista = useMemo(() => {
    if (!debeConsultarLista) return undefined
    return lista.rows.find(r => r.salesid === salesid && r.dataareaid === empresa)
  }, [debeConsultarLista, lista.rows, salesid, empresa])

  const { cabecera, origen } = useMemo<{
    cabecera: PedidoVentaCabeceraVM | null
    origen: OrigenCabeceraPedido | null
  }>(() => {
    if (filaDelEstado) return { cabecera: deLaFila(filaDelEstado), origen: 'state' }
    if (deLaLista)     return { cabecera: deLaFila(deLaLista),     origen: 'lista' }
    // Tercer escalón: `cabeceraDeLineas`, que compone la página cuando ya las
    // tiene. Aquí todavía no se puede.
    return { cabecera: null, origen: null }
  }, [filaDelEstado, deLaLista])

  return {
    cabecera,
    origen,
    volverA: estado?.from,
    // Sólo se está «cargando» mientras no haya nada que enseñar: si la fila vino
    // en el state, la pantalla se pinta entera desde el primer frame.
    isLoading: !cabecera && debeConsultarLista && lista.isLoading,
    isError: !cabecera && debeConsultarLista && lista.isError,
    refetch: lista.refetch,
  }
}
